import {
  KNOB_GROUPS,
  formatKnobValue,
  getPath,
  knobsFor,
  type AnimationOptions,
  type AnimationVariant,
  type ColorKnob,
  type ColorListKnob,
  type KnobValue,
  type PreviewTemplate,
  type RangeKnob,
  type ToggleKnob,
} from '@game-platform/pack-animation';
import { Checkbox } from '../../components/ui/Checkbox';
import { ColorField } from '../../components/ui/ColorField';
import { RangeInput } from '../../components/ui/RangeInput';
import { ColorListField } from './ColorListField';
import { knobLock } from './knobLock';

interface Props {
  variant: AnimationVariant;
  /** Hides the knobs this template ignores. */
  template: PreviewTemplate;
  options: AnimationOptions | undefined;
  onKnobChange: (path: string, value: KnobValue) => void;
}

function rangeValue(knob: RangeKnob, options: AnimationOptions | undefined): number {
  const set = getPath(options, knob.path);
  return typeof set === 'number' ? set : knob.defaultValue;
}

function colorValue(knob: ColorKnob, options: AnimationOptions | undefined): string {
  const set = getPath(options, knob.path);
  return typeof set === 'string' ? set : knob.defaultValue;
}

function toggleValue(knob: ToggleKnob, options: AnimationOptions | undefined): boolean {
  const set = getPath(options, knob.path);
  return typeof set === 'boolean' ? set : knob.defaultValue;
}

function colorListValue(knob: ColorListKnob, options: AnimationOptions | undefined): readonly string[] {
  const set = getPath(options, knob.path);
  return Array.isArray(set) && set.every((item) => typeof item === 'string') ? set : knob.defaultValue;
}

/** The curated knobs of the active mechanic, grouped; untouched knobs sit at the preset value. */
export function KnobsPanel({ variant, template, options, onKnobChange }: Props) {
  const knobs = knobsFor(variant, template);

  return (
    <div className="flex flex-col gap-5">
      {KNOB_GROUPS.map((group) => {
        const inGroup = knobs.filter((knob) => knob.group === group);
        if (inGroup.length === 0) return null;
        return (
          <section key={group} aria-label={group}>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]">
              {group}
            </h3>
            <div className="flex flex-col gap-3">
              {inGroup.map((knob) => {
                const touched = getPath(options, knob.path) !== undefined;
                const lock = knobLock(knob, knobs, options);
                const disabled = lock !== null;
                return (
                  // On the row, not the control: a disabled input shows no tooltip
                  <div key={knob.path} title={lock ?? undefined}>
                    <div className="mb-1 flex items-center gap-1.5 text-[12px] text-[var(--co-text)]">
                      <span>{knob.label}</span>
                      {touched && (
                        <span
                          aria-label="changed"
                          className="h-1.5 w-1.5 rounded-full bg-[var(--co-info)]"
                        />
                      )}
                    </div>
                    {knob.kind === 'range' ? (
                      <RangeInput
                        aria-label={knob.label}
                        value={rangeValue(knob, options)}
                        min={knob.min}
                        max={knob.max}
                        step={knob.step}
                        disabled={disabled}
                        onChange={(value) => onKnobChange(knob.path, value)}
                        format={(value) => formatKnobValue(knob, value)}
                      />
                    ) : knob.kind === 'toggle' ? (
                      <Checkbox
                        aria-label={knob.label}
                        label="On"
                        checked={toggleValue(knob, options)}
                        disabled={disabled}
                        onChange={(value) => onKnobChange(knob.path, value)}
                      />
                    ) : knob.kind === 'colorList' ? (
                      <ColorListField
                        aria-label={knob.label}
                        value={colorListValue(knob, options)}
                        min={knob.min}
                        max={knob.max}
                        disabled={disabled}
                        onChange={(value) => onKnobChange(knob.path, value)}
                      />
                    ) : (
                      <ColorField
                        aria-label={knob.label}
                        value={colorValue(knob, options)}
                        disabled={disabled}
                        onChange={(value) => onKnobChange(knob.path, value)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
